import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Mail, Lock } from 'lucide-react'
import { AuthService } from '../utils/authService'
import { useUserProfile } from '../context/UserProfileContext'
import BritLogo from '../assets/white-brit-logo.svg'
import { CustomOTPInput } from '../components/CustomOtp'

enum AdminLoginStep {
  CREDENTIALS = 'CREDENTIALS',
  OTP = 'OTP',
}

export default function AdminLoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { refetchUser } = useUserProfile()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [otp, setOtp] = useState('')
  const [step, setStep] = useState<AdminLoginStep>(AdminLoginStep.CREDENTIALS)

  useEffect(() => {
    const storedEmail = localStorage.getItem('adminLoginEmail')
    if (storedEmail) {
      setEmail(storedEmail)
      setStep(AdminLoginStep.OTP)
    }
    if (location.state?.message) {
      setSuccessMessage(location.state.message)
      window.history.replaceState({}, document.title)
    }
  }, [location])

  const handleCredentialsSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)

    if (!email || !password) {
      setError('Please enter email and password.')
      return
    }

    try {
      setIsLoading(true)
      const response = await AuthService.login({ type: 'email', email, password })
      if (response.statusCode === 200) {
        localStorage.setItem('adminLoginEmail', email)
        setStep(AdminLoginStep.OTP)
        setSuccessMessage(response.response?.message || 'OTP sent to your email')
      } else {
        setError(response.message || 'Unable to sign in. Check your credentials.')
      }
    } catch (err: any) {
      const message =
        err?.response?.message || err?.message || 'Unable to sign in. Please try again.'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleOtpSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit code.')
      return
    }

    try {
      setIsLoading(true)
      const response = await AuthService.loginVerifyOtp({
        type: 'email',
        email,
        otp,
      })

      if (response.statusCode === 200) {
        const { accessToken, refreshToken } = response.response
        AuthService.setTokens(accessToken, refreshToken)
        localStorage.removeItem('adminLoginEmail')
        await refetchUser()
        navigate('/admin/dashboard')
      } else {
        setError(response.message || 'OTP verification failed. Please try again.')
      }
    } catch (err: any) {
      const message =
        err?.response?.message || err?.message || 'Unable to verify OTP. Please try again.'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className='min-h-screen grid grid-cols-1 md:grid-cols-2'>
      {/* Left panel — banner on mobile, full panel on desktop */}
      <div className='relative flex items-center justify-center bg-[#0f0b10] py-8 md:py-0 md:min-h-screen'>
        <div className='absolute inset-0 bg-gradient-to-br from-[#0f0b10] via-[#140b14] to-[#1d0f12]' />
        <div className='absolute inset-0 opacity-40'
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.04) 0, transparent 35%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.05) 0, transparent 35%)',
          }}
        />
        <div className='relative flex items-center gap-3 px-6 sm:px-8'>
          <img src={BritLogo} alt='BritFiler' className='h-10 sm:h-12 w-auto' />
          <div className='h-8 sm:h-10 w-px bg-white/40' />
          <p className='text-white text-lg sm:text-xl font-semibold'>Admin</p>
        </div>
      </div>

      {/* Right panel */}
      <div className='flex items-center justify-center bg-[#f5f8fb] px-4 sm:px-6 py-8 sm:py-12'>
        <div className='w-full max-w-md'>
          <div className='mb-6 sm:mb-8'>
            <h1 className='text-2xl md:text-3xl font-bold text-gray-900 text-start'>
              {step === AdminLoginStep.CREDENTIALS ? 'Sign In To Your Account' : 'Verify OTP'}
            </h1>
            <p className='text-sm text-gray-600 mt-2 text-start'>
              {step === AdminLoginStep.CREDENTIALS
                ? 'Welcome back! Please enter your details.'
                : 'Enter the 6-digit code sent to your email.'}
            </p>
          </div>

          {error && (
            <div className='mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700'>
              {error}
            </div>
          )}

          {successMessage && (
            <div className='mb-4 rounded-md bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700'>
              {successMessage}
            </div>
          )}

          {step === AdminLoginStep.CREDENTIALS ? (
            <form onSubmit={handleCredentialsSubmit} className='space-y-4'>
              <div className='space-y-1'>
                <div className='text-sm font-medium text-gray-700 text-start'>Email</div>
                <div className='flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2'>
                  <Mail className='h-4 w-4 text-gray-400' />
                  <input
                    type='email'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder='olivia@untitledui.com'
                    className='w-full text-sm text-gray-900 placeholder:text-gray-400 outline-none'
                  />
                </div>
              </div>

              <div className='space-y-1'>
                <div className='text-sm font-medium text-gray-700 text-start'>Password</div>
                <div className='flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2'>
                  <Lock className='h-4 w-4 text-gray-400' />
                  <input
                    type='password'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder='••••••••'
                    className='w-full text-sm text-gray-900 placeholder:text-gray-400 outline-none'
                  />
                </div>
                <div className='flex justify-end'>
                  <Link to='/forgot-password' className='text-sm font-semibold text-gray-700 hover:text-gray-900'>
                    Forgot password?
                  </Link>
                </div>
              </div>

              <button
                type='submit'
                disabled={isLoading}
                className='w-full rounded-md bg-[#0b1f3a] px-4 py-3 text-sm font-semibold text-white hover:bg-[#0a1a31] transition disabled:opacity-60'
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit} className='space-y-6'>
              <div className='space-y-2'>
                <p className='text-sm text-gray-700 text-start break-words'>We sent a 6-digit code to {email}</p>
                <CustomOTPInput value={otp} onChange={setOtp} length={6} />
              </div>
              <div className='flex flex-col-reverse sm:flex-row items-stretch sm:items-center sm:justify-between gap-3'>
                <button
                  type='button'
                  onClick={() => setStep(AdminLoginStep.CREDENTIALS)}
                  className='text-sm font-semibold text-gray-700 hover:text-gray-900 py-2 sm:py-0'
                >
                  Back
                </button>
                <button
                  type='submit'
                  disabled={isLoading}
                  className='rounded-md bg-[#0b1f3a] px-4 py-3 text-sm font-semibold text-white hover:bg-[#0a1a31] transition disabled:opacity-60'
                >
                  {isLoading ? 'Verifying...' : 'Verify OTP'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
